import { App, Space, Tag, Typography } from 'antd'
import type { ReactNode } from 'react'

import type { PermissionDiff } from '../permissionDiff'

function ScopeList({
  label,
  scopes,
  color,
}: {
  label: string
  scopes: string[]
  color: string
}) {
  if (!scopes.length) return null
  return (
    <div style={{ marginBottom: 12 }}>
      <Typography.Text strong>
        {label} ({scopes.length})
      </Typography.Text>
      <Space wrap style={{ display: 'flex', marginTop: 4 }}>
        {scopes.map((scope) => (
          <Tag key={scope} color={color}>
            {scope}
          </Tag>
        ))}
      </Space>
    </div>
  )
}

const pluralize = (count: number) =>
  `${count} permission${count === 1 ? '' : 's'}`

/** Confirmation dialogs gating every role mutation. */
export function useRoleConfirm() {
  const { modal, message } = App.useApp()

  const confirm = (
    title: string,
    content: ReactNode,
    okText: string,
    action: () => Promise<void>,
    fallbackError: string,
  ) => {
    void modal.confirm({
      title,
      content,
      okText,
      width: 520,
      onOk: async () => {
        try {
          await action()
        } catch (error) {
          void message.error(
            error instanceof Error ? error.message : fallbackError,
          )
        }
      },
    })
  }

  const confirmCreate = (
    roleName: string,
    permissionCount: number,
    action: () => Promise<void>,
  ) =>
    confirm(
      `Create role "${roleName}"?`,
      `The role will be created with ${pluralize(permissionCount)}.`,
      'Create role',
      action,
      'Failed to create role',
    )

  const confirmDescription = (roleName: string, action: () => Promise<void>) =>
    confirm(
      `Save description of role "${roleName}"?`,
      'The new description replaces the current one.',
      'Save description',
      action,
      'Failed to update role',
    )

  const confirmPermissions = (
    roleName: string,
    diff: PermissionDiff,
    action: () => Promise<void>,
  ) =>
    confirm(
      `Change permissions of role "${roleName}"?`,
      <>
        <Typography.Paragraph>
          This changes access for every user with this role.
        </Typography.Paragraph>
        <ScopeList label="Added" scopes={diff.added} color="green" />
        <ScopeList label="Removed" scopes={diff.removed} color="red" />
      </>,
      'Save permissions',
      action,
      'Failed to update permissions',
    )

  return { confirmCreate, confirmDescription, confirmPermissions }
}
